import type {} from '@mui/lab/themeAugmentation'
import { type Theme, createTheme } from '@mui/material/styles'

import { themeProps } from '@oxfordmedicalsimulation/oms-fe-components'

import { colors } from './colors'
import { typography } from './typography'

export const themeVariables = {}
export const theme: Theme = createTheme({
  ...themeProps,
  breakpoints: {
    values: {
      xs: 576,
      sm: 768,
      md: 992,
      lg: 1200,
      xl: 1440,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: (_theme) => ({
        html: {
          height: '100%',
          '@media print': {
            height: 'auto',
          },
        },
        body: {
          height: '100%',
          '@media print': {
            height: 'auto',
          },
          '#app': {
            overflow: 'visible',
            height: '100%',
            '@media print': {
              height: 'auto',
            },
          },
        },
      }),
    },
    MuiAppBar: {
      variants: [
        {
          props: { variant: 'header' },
          style: () => ({
            background: colors.white,
          }),
        },
        {
          props: { variant: 'footer' },
          style: () => ({
            background: colors.footerBackground,
          }),
        },
      ],
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          // Apply only to labels that are not inside a FormControl
          ':not(.MuiInputLabel-root)': {
            fontSize: '16px',
            lineHeight: '18px',
            fontWeight: 700,
            color: colors.greyText,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          padding: '5px 15px',
          fontSize: 14,
          variants: [
            {
              props: { size: 'small' },
              style: {
                '& .MuiButton-icon': {
                  'svg:nth-of-type(1)': {
                    fontSize: 16,
                  },
                },
              },
            },
            {
              props: { size: 'medium' },
              style: {
                '& .MuiButton-icon': {
                  'svg:nth-of-type(1)': {
                    fontSize: 16,
                  },
                },
              },
            },
            {
              props: { size: 'large' },
              style: {
                '& .MuiButton-icon': {
                  'svg:nth-of-type(1)': {
                    fontSize: 18,
                  },
                },
              },
            },
          ],
        },
        sizeLarge: {
          padding: '7.5px 19px',
          fontSize: 18,
        },
      },
    },
    MuiLink: {
      variants: [
        {
          props: { variant: 'link1' },
          style: () => ({
            color: colors.linkColor,
            borderBottom: `1px solid ${colors.linkColor}`,
            textDecoration: 'none',
            ':hover': { backgroundColor: colors.linkBackgroundHover, borderBottomWidth: '3px' },
          }),
        },
        {
          props: { variant: 'link2' },
          style: () => ({
            color: 'inherit',
            textDecoration: 'none',
            ':hover': { textDecoration: 'underline' },
          }),
        },
      ],
    },
    /**
     * Design system MuiCard
     * https://www.figma.com/file/QUsCA3QhrjQUCWdPstSTVO/OMS-NEW-MUI-Pro-v5.9.0?type=design&node-id=11881-89985&t=Cs0zAJLhIwrIdBcW-0
     * https://www.figma.com/file/tG2nVZ4xS4YV0xUrqoiYWK/Rooms-%26-Roles-Setup-Sandbox?node-id=1639%3A152211
     **/
    MuiCard: {
      variants: [
        {
          props: { variant: 'shadowBlur' },
          style: {
            border: 'none',
            boxShadow: `0px 0px 10px 2px ${colors.romanSlate20}`,
            backdropFilter: 'blur(2.5px)',
            position: 'relative', // enables absolute values within
          },
        },
      ],
    },

    /**
     * Design system MuiChip
     * https://www.figma.com/file/QUsCA3QhrjQUCWdPstSTVO/OMS-NEW-MUI-Pro-v5.9.0?node-id=11291-81968&t=pkhfRHujmGe0umaj-0
     **/
    MuiChip: {
      variants: [
        {
          props: { variant: 'tag' },
          style: {
            fontSize: '12px',
            fontWeight: 500,
            letterSpacing: 0.1,
            height: '22px',
            '& .MuiChip-label': {
              paddingLeft: '10px',
              paddingRight: '10px',
            },
          },
        },
      ],
      styleOverrides: {
        icon: {
          fontSize: '12px',
        },
        sizeSmall: {
          fontSize: '12px',
        },
      },
    },
    SearchBar: {
      // Can override style props
      styleOverrides: {
        root: {
          border: 'solid 1px #d8dde0',
          backgroundColor: '#FBFAFA',
        },
        tag: {
          defaultProps: {
            color: 'secondary',
          },
          variants: [
            // Style free-solo tags
            {
              props: (props) => {
                return typeof props.option === 'string'
              },
              style: ({ theme }) => {
                return {
                  backgroundColor: colors.secondaryMain,
                  color: theme.palette.common.white,
                }
              },
            },
          ],
        },
        option: {
          variants: [
            {
              props: (props) => {
                return props.selected === false
              },
              style: () => {
                return {
                  backgroundColor: colors.romanSlate20,
                }
              },
            },
            {
              props: (props) => {
                return props.selected === true
              },
              style: () => {
                return {
                  backgroundColor: colors.romanSlate40,
                }
              },
            },
          ],
        },
      },
    },
  },
  palette: {
    common: { black: colors.black, white: colors.white },
    primary: {
      main: colors.primaryMain,
      dark: colors.primaryDark,
      light: colors.primaryLight,
      contrastText: colors.white,
    },
    secondary: { main: colors.secondaryMain, contrastText: colors.white },
    info: { main: colors.infoMain, dark: colors.infoDark, light: colors.infoLight, contrastText: colors.white },
    error: { main: colors.errorMain, dark: colors.errorDark, light: colors.errorLight, contrastText: colors.white },
    warning: {
      main: colors.warningMain,
      dark: colors.warningDark,
      light: colors.warningLight,
      contrastText: colors.white,
    },
    success: {
      main: colors.successMain,
      dark: colors.successDark,
      light: colors.successLight,
      contrastText: colors.white,
    },
  },
  typography,
})
